import { useContext, useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';
import { SessionContext } from '@wix/da-react-context/pkg/publicSession';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import Modal from '@wix/da-shared-react/pkg/Popup/Modal';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import PointsCard from '@wix/da-shared-react/pkg/PointsCard';
import { PurchasePointsCardClickBiEvent, BiData } from '@wix/da-bi/pkg/events';
import {
  getVideoCreatorPointsPackages,
  getVideoCreatorRestrictions,
} from '../../selectors/studio/videoCreator';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { Url, URLS } from '@wix/da-url';

import s from './VideoCreatorPointsModal.scss';

export interface Props {
  isOpen: boolean;
  onRequestClose: () => void;
}

type modalVersion = 'nonCore' | 'coreNoMax' | 'coreMax';

const VideoCreatorPointsModal: FC<Props> = ({ isOpen, onRequestClose }) => {
  const { t } = useTranslation();
  const { hasCoreMembership } = useContext(SessionContext);
  const coreLevel = useSelector(getCoreLevel);
  const pointsPackages = useSelector(getVideoCreatorPointsPackages);
  const { pointsBalance } = useSelector(getVideoCreatorRestrictions);
  const formattedPointsBalance =
    Intl.NumberFormat('en-US').format(pointsBalance);
  const version: modalVersion = useMemo(() => {
    if (!hasCoreMembership) {
      return 'nonCore';
    } else {
      if (coreLevel === 'core_max') {
        return 'coreMax';
      } else {
        return 'coreNoMax';
      }
    }
  }, [coreLevel, hasCoreMembership]);
  const modalHeight = version !== 'coreMax' ? 646 : 446;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      width={690}
      height={modalHeight}
      className={s['modal']}
      contentClassName={s['content']}
    >
      <div
        className={cn(
          s['root'],
          version === 'nonCore' && s['non-core'],
          version === 'coreNoMax' && s['core-no-max'],
          version === 'coreMax' && s['core-max']
        )}
      >
        <div className={s['head-area']}>
          <Heading className={s['heading']}>
            {t('studio.videoCreator.pointsModal.getMore')}
          </Heading>
        </div>
        <div className={s['points-cards']}>
          {pointsPackages.map((pointsPackage, index) => (
            <PointsCard
              key={index}
              pointsAmount={pointsPackage.points}
              price={pointsPackage.price}
              subproductId={pointsPackage.subproductId}
              biData={BiData<PurchasePointsCardClickBiEvent>({
                evid: 919,
                points_total: pointsPackage.points,
                subproductid: pointsPackage.subproductId,
              })}
            />
          ))}
        </div>
        {version !== 'coreMax' && (
          <div className={s['core-upsell']}>
            <Heading className={s['upsell-heading']}>
              {t('studio.videoCreator.pointsModal.saveHeading')}
            </Heading>
            <div className={s['upsell-detail']}>
              {t('studio.videoCreator.pointsModal.saveDetail')}
            </div>
            <BrandedButton
              size="medium"
              variant="core-orange"
              className={s['upsell-button']}
              href={Url.coreMembershipLink()}
              target="_blank"
            >
              {t('studio.videoCreator.pointsModal.upgradeButton')}
            </BrandedButton>
          </div>
        )}
        <div className={s['fine-print']}>
          <div className={s['balance']}>
            <span className={s['points-label']}>
              {t('studio.videoCreator.pointsModal.balance')}
            </span>
            <span className={s['points-value']}>
              {`${formattedPointsBalance} ${t('studio.videoCreator.pointsLabel')}`}
            </span>
          </div>
          <div className={s['legal']}>
            <a
              className={cn(s['link'], s['tos-link'])}
              href={URLS.POINTS_POLICY}
              target="_blank"
              rel="noreferrer"
            >
              {t('studio.videoCreator.pointsModal.pointsTOS')}
            </a>
            <a
              className={s['link']}
              href={URLS.ABOUT_POINTS}
              target="_blank"
              rel="noreferrer"
            >
              {t('studio.videoCreator.pointsModal.pointsLearn')}
            </a>
          </div>
        </div>
      </div>
    </Modal>
  );
};

VideoCreatorPointsModal.displayName = 'VideoCreatorPointsModal';
export default VideoCreatorPointsModal;
